import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import styled from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS } from '@sorare/core/src/atoms/typography';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { isTypeFn } from '@sorare/core/src/lib/gql';
import { mergeRewardsConfig } from '@sorare/core/src/lib/rewards';

import { FixtureOverviewTitle } from 'components/fixture/FixtureTeamsOverview/FixtureOverview/FixtureOverviewTitle';

import { NeonRewards } from '../NeonRewards';
import type {
  RewardConfigsOverviewHeader_anyRewardConfigInterface,
  RewardConfigsOverviewHeader_rarityRewardConfigs,
  RewardConfigsOverviewHeader_so5Fixture,
} from './__generated__/index.graphql';

const StyledIconButton = styled(IconButton)`
  margin-left: auto;
`;

type Props = {
  so5Fixture?: RewardConfigsOverviewHeader_so5Fixture;
  rewardConfigs: RewardConfigsOverviewHeader_anyRewardConfigInterface[];
  boostedRewardConfigsByRarity?: RewardConfigsOverviewHeader_rarityRewardConfigs[];
  onlyRewards?: boolean;
  verificationNeeded?: { phoneNumber: boolean; kyc: boolean };
  onClose?: () => void;
  myCompletedThresholdsStreakTasksCount?: number;
  myCommonCompletedThresholdsStreakTasksCount?: number;
};
export const RewardConfigsOverviewHeader = ({
  so5Fixture,
  rewardConfigs,
  boostedRewardConfigsByRarity,
  onlyRewards,
  verificationNeeded,
  onClose,
  myCompletedThresholdsStreakTasksCount,
  myCommonCompletedThresholdsStreakTasksCount,
}: Props) => {
  const isLaptopAndAbove = useIsLaptop();

  const boostedRewardConfigs =
    boostedRewardConfigsByRarity?.flatMap(reward => reward.rewardConfigs) || [];

  const cashRewardConfigs = rewardConfigs.filter(
    isTypeFn('MonetaryRewardConfig')
  );
  const boxRewardConfigs = rewardConfigs.filter(
    isTypeFn('ProbabilisticBundleRewardConfig')
  );
  const cardRewardConfigs = rewardConfigs.filter(isTypeFn('CardRewardConfig'));
  const shardRewardConfigs = rewardConfigs.filter(
    isTypeFn('CardShardRewardConfig')
  );

  const mainRewardsPresent =
    shardRewardConfigs.length > 0 ||
    cardRewardConfigs.length > 0 ||
    cashRewardConfigs.length > 0 ||
    boxRewardConfigs.length > 0;

  return (
    <>
      {!isLaptopAndAbove && !!onClose && (
        <StyledIconButton
          icon={faTimes}
          color="transparent"
          size="compact"
          onClick={onClose}
        />
      )}
      {!onlyRewards && !!so5Fixture && (
        <Vertical center gap={2.5}>
          <StarBall width={40} height={40} />
          <BrandS color="var(--c-nd-600)">
            <FixtureOverviewTitle so5Fixture={so5Fixture} />
          </BrandS>
        </Vertical>
      )}
      {!!mainRewardsPresent && (
        <NeonRewards
          verificationNeeded={verificationNeeded}
          anyRewardConfigInterfaces={mergeRewardsConfig([
            ...rewardConfigs,
            ...boostedRewardConfigs,
          ])}
          myCompletedThresholdsStreakTasksCount={
            myCompletedThresholdsStreakTasksCount
          }
          myCommonCompletedThresholdsStreakTasksCount={
            myCommonCompletedThresholdsStreakTasksCount
          }
          size={32}
        />
      )}
    </>
  );
};

RewardConfigsOverviewHeader.fragments = {
  so5Fixture: gql`
    fragment RewardConfigsOverviewHeader_so5Fixture on So5Fixture {
      slug
      id
      ...FixtureOverviewTitle_so5Fixture
    }
    ${FixtureOverviewTitle.fragments.so5Fixture}
  ` as TypedDocumentNode<RewardConfigsOverviewHeader_so5Fixture>,
  anyRewardConfigInterface: gql`
    fragment RewardConfigsOverviewHeader_anyRewardConfigInterface on AnyRewardConfigInterface {
      id
      ...NeonRewards_anyRewardConfigInterface
      ...mergeRewardsConfig_anyRewardConfigInterface
    }
    ${NeonRewards.fragments.anyRewardConfigInterface}
    ${mergeRewardsConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<RewardConfigsOverviewHeader_anyRewardConfigInterface>,
  rarityRewardConfigs: gql`
    fragment RewardConfigsOverviewHeader_rarityRewardConfigs on RarityRewardConfigs {
      rewardConfigs {
        id
        ...NeonRewards_anyRewardConfigInterface
        ...mergeRewardsConfig_anyRewardConfigInterface
      }
    }
    ${NeonRewards.fragments.anyRewardConfigInterface}
    ${mergeRewardsConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<RewardConfigsOverviewHeader_rarityRewardConfigs>,
};
